// file name: commentService.ts
import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp,
  deleteDoc,
  updateDoc,
  limit
} from 'firebase/firestore';
import { db } from './config';

export const COLLECTIONS = {
  COMMENTS: 'comments',
  NEWS: 'news'
};

export type CommentTargetType = 'news' | 'program';

export interface CommentData {
  id?: string;
  targetId: string;
  targetType: CommentTargetType;
  newsId?: string; // Legacy field for news comments
  programId?: string;
  userId: string;
  userEmail: string;
  userName: string;
  userPhotoURL?: string;
  content: string;
  likes?: number;
  dislikes?: number;
  isEdited?: boolean;
  isApproved?: boolean;
  createdAt: any;
  updatedAt?: any;
  parentCommentId?: string; // For nested comments/replies
}

export interface CreateCommentInput extends Omit<CommentData, 'id' | 'createdAt' | 'targetId' | 'targetType'> {
  targetId?: string;
  targetType?: CommentTargetType;
}

const toIsoString = (value: any): string => {
  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return new Date().toISOString();
};

const toMillis = (value: any): number => {
  if (value?.toDate && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const mapDocToComment = (docSnap: any): CommentData => {
  const data = docSnap.data();
  const resolvedTargetType: CommentTargetType = data.targetType || (data.programId ? 'program' : 'news');
  const resolvedTargetId = data.targetId || data.newsId || data.programId || '';

  return {
    id: docSnap.id,
    targetId: resolvedTargetId,
    targetType: resolvedTargetType,
    newsId: data.newsId,
    programId: data.programId,
    userId: data.userId,
    userEmail: data.userEmail,
    userName: data.userName,
    userPhotoURL: data.userPhotoURL,
    content: data.content,
    likes: data.likes || 0,
    dislikes: data.dislikes || 0,
    isEdited: data.isEdited || false,
    isApproved: data.isApproved || false,
    parentCommentId: data.parentCommentId,
    createdAt: toIsoString(data.createdAt),
    updatedAt: data.updatedAt ? toIsoString(data.updatedAt) : undefined
  };
};

// Create a new comment
export const createComment = async (commentData: CreateCommentInput): Promise<{ success: boolean; data?: CommentData; error?: string }> => {
  try {
    const targetType: CommentTargetType = commentData.targetType || (commentData.programId ? 'program' : 'news');
    const targetId = commentData.targetId || commentData.newsId || commentData.programId;

    if (!targetId) {
      throw new Error('targetId is required to create a comment');
    }

    const writePayload: any = {
      ...commentData,
      targetId,
      targetType,
      likes: 0,
      dislikes: 0,
      isEdited: false,
      isApproved: true, // Auto-approve comments for now
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Keep legacy fields for compatibility with existing reads/indexes.
    if (targetType === 'news') {
      writePayload.newsId = targetId;
    }
    if (targetType === 'program') {
      writePayload.programId = targetId;
    }

    const sanitizedPayload = Object.fromEntries(
      Object.entries(writePayload).filter(([, value]) => value !== undefined)
    );

    const commentRef = await addDoc(collection(db, COLLECTIONS.COMMENTS), {
      ...sanitizedPayload
    });

    return {
      success: true,
      data: {
        ...commentData,
        targetId,
        targetType,
        newsId: targetType === 'news' ? targetId : commentData.newsId,
        programId: targetType === 'program' ? targetId : commentData.programId,
        id: commentRef.id,
        createdAt: new Date().toISOString(),
        likes: 0,
        dislikes: 0,
        isEdited: false,
        isApproved: true
      }
    };
  } catch (error: any) {
    console.error('Error creating comment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get comments for a specific target (news/program)
export const getCommentsByTarget = async (
  targetId: string,
  targetType: CommentTargetType = 'news',
  limitCount: number = 20
): Promise<{ success: boolean; data: CommentData[]; error?: string }> => {
  if (!targetId) {
    return { success: true, data: [] };
  }

  if (targetType === 'news') {
    return getCommentsByNewsId(targetId, limitCount);
  }

  try {
    // Query by `programId` to avoid requiring new composite indexes.
    const programQuery = query(
      collection(db, COLLECTIONS.COMMENTS),
      where('programId', '==', targetId),
      limit(Math.max(limitCount * 3, limitCount))
    );

    const querySnapshot = await getDocs(programQuery);
    const comments: CommentData[] = [];
    querySnapshot.forEach((docSnap) => {
      const comment = mapDocToComment(docSnap);
      if (comment.isApproved) {
        comments.push(comment);
      }
    });

    comments.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

    return {
      success: true,
      data: comments.slice(0, limitCount)
    };
  } catch (error: any) {
    console.error('Error fetching comments:', error);
    return {
      success: false,
      data: [],
      error: error.message
    };
  }
};

// Get comments for a specific news article
export const getCommentsByNewsId = async (newsId: string, limitCount: number = 20): Promise<{ success: boolean; data: CommentData[]; error?: string }> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COMMENTS),
      where('newsId', '==', newsId),
      where('isApproved', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const comments: CommentData[] = [];
    querySnapshot.forEach((docSnap) => {
      comments.push(mapDocToComment(docSnap));
    });

    return {
      success: true,
      data: comments
    };
  } catch (error: any) {
    console.error('Error fetching comments:', error);
    return {
      success: false,
      data: [],
      error: error.message
    };
  }
};

// Get comment count for a target (news/program)
export const getCommentCountByTarget = async (
  targetId: string,
  targetType: CommentTargetType = 'news'
): Promise<{ success: boolean; count: number; error?: string }> => {
  if (!targetId) {
    return { success: true, count: 0 };
  }

  if (targetType === 'news') {
    return getCommentCount(targetId);
  }

  try {
    // Query by `programId` to avoid requiring new composite indexes.
    const programQuery = query(
      collection(db, COLLECTIONS.COMMENTS),
      where('programId', '==', targetId)
    );

    const programSnapshot = await getDocs(programQuery);
    let count = 0;
    programSnapshot.forEach((docSnap) => {
      if (docSnap.data().isApproved) {
        count += 1;
      }
    });

    return {
      success: true,
      count
    };
  } catch (error: any) {
    console.error('Error counting comments:', error);
    return {
      success: false,
      count: 0,
      error: error.message
    };
  }
};

// Get comment count for a news article
export const getCommentCount = async (newsId: string): Promise<{ success: boolean; count: number; error?: string }> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COMMENTS),
      where('newsId', '==', newsId),
      where('isApproved', '==', true)
    );

    const querySnapshot = await getDocs(q);
    return {
      success: true,
      count: querySnapshot.size
    };
  } catch (error: any) {
    console.error('Error counting comments:', error);
    return {
      success: false,
      count: 0,
      error: error.message
    };
  }
};

// Update a comment
export const updateComment = async (commentId: string, updates: { content: string }): Promise<{ success: boolean; error?: string }> => {
  try {
    const commentRef = doc(db, COLLECTIONS.COMMENTS, commentId);
    await updateDoc(commentRef, {
      ...updates,
      isEdited: true,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error updating comment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete a comment
export const deleteComment = async (commentId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.COMMENTS, commentId));
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting comment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Like/Dislike a comment
export const rateComment = async (commentId: string, action: 'like' | 'dislike', userId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // Note: In a production app, you'd want to track which users have liked/disliked
    // to prevent multiple votes. This is a simplified version.
    const commentRef = doc(db, COLLECTIONS.COMMENTS, commentId);
    const increment = action === 'like' ? 1 : -1;
    
    await updateDoc(commentRef, {
      [action === 'like' ? 'likes' : 'dislikes']: increment,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error rating comment:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get recent comments for admin dashboard
export const getRecentComments = async (limitCount: number = 20): Promise<{ success: boolean; data: CommentData[]; error?: string }> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.COMMENTS),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const comments: CommentData[] = [];

    querySnapshot.forEach((docSnap) => {
      comments.push(mapDocToComment(docSnap));
    });

    return {
      success: true,
      data: comments
    };
  } catch (error: any) {
    console.error('Error fetching recent comments:', error);
    return {
      success: false,
      data: [],
      error: error.message
    };
  }
};
